import { Component, Input, OnInit } from '@angular/core';
import { CompanyResponse, MarketDealResponse } from '../../../../../../@shared/models/response.module';
import { EndpointService } from '../../../../../../@shared/endpoints.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../../../@core/services/toast.service';

@Component({
  selector: 'app-add-deal',
  standalone: false,
  templateUrl: './add-deal.html',
  styleUrl: './add-deal.css'
})
export class AddDeal implements OnInit {
  @Input() company: CompanyResponse;
  @Input() editMode: boolean = false;
  @Input() deal: MarketDealResponse | null = null;

  title = '';
  description = '';
  start_date = '';
  end_date = '';
  discount_code = '';
  discount_percentage: number | null = null;
  imageFile: File | null = null;

  saving = false;
  submitted = false;

  // Validation helpers
  get isTitleInvalid(): boolean {
    return this.submitted && !this.title.trim();
  }

  get isStartDateInvalid(): boolean {
    return this.submitted && !this.start_date;
  }

  get isEndDateInvalid(): boolean {
    return this.submitted && !this.end_date;
  }

  get isDiscountCodeInvalid(): boolean {
    return this.submitted && !this.discount_code.trim();
  }

  get isFormValid(): boolean {
    return !!this.title.trim() && !!this.start_date && !!this.end_date && !!this.discount_code.trim();
  }

  constructor(
    private endpointService: EndpointService,
    private modal: NgbActiveModal,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    if (this.editMode && this.deal) {
      this.title = this.deal.title || '';
      this.description = this.deal.description || '';
      this.start_date = this.deal.start_date ? this.formatDateForInput(this.deal.start_date) : '';
      this.end_date = this.deal.end_date ? this.formatDateForInput(this.deal.end_date) : '';
      this.discount_code = this.deal.discount_code || '';
      this.discount_percentage = this.deal.discount_percentage || null;
    }
  }

  formatDateForInput(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  onSave() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('companyProfile.addDeal.validationError'));
      return;
    }

    if (this.saving) return;
    this.saving = true;

    if (this.editMode && this.deal) {
      // Update existing deal
      this.endpointService.cor_coy_52(this.deal.deal_id, {
        company_id: this.company.company_id,
        title: this.title.trim(),
        description: this.description.trim() || undefined,
        start_date: this.start_date,
        end_date: this.end_date,
        discount_code: this.discount_code.trim(),
        discount_percentage: this.discount_percentage || undefined
      }).subscribe({
        next: (res) => {
          // Upload image if provided
          if (this.imageFile && this.deal) {
            this.endpointService.cor_coy_54(
              this.deal.deal_id,
              this.company.company_id,
              this.imageFile
            ).subscribe({
              next: () => {
                this.saving = false;
                this.toastService.success(this.translateService.instant('companyProfile.addDeal.updateSuccess'));
                this.modal.close(res.data);
              },
              error: () => {
                this.saving = false;
                this.toastService.success(this.translateService.instant('companyProfile.addDeal.updateSuccess'));
                this.modal.close(res.data);
              }
            });
          } else {
            this.saving = false;
            this.toastService.success(this.translateService.instant('companyProfile.addDeal.updateSuccess'));
            this.modal.close(res.data);
          }
        },
        error: (err) => {
          this.saving = false;
          this.toastService.error(err.error?.reason || this.translateService.instant('companyProfile.addDeal.updateError'));
        }
      });
    } else {
      // Create new deal
      this.endpointService.cor_coy_51({
        company_id: this.company.company_id,
        title: this.title.trim(),
        description: this.description.trim() || undefined,
        start_date: this.start_date,
        end_date: this.end_date,
        discount_code: this.discount_code.trim(),
        discount_percentage: this.discount_percentage || undefined
      }).subscribe({
        next: (res) => {
          const newDeal = res.data;
          // Upload image if provided
          if (this.imageFile && newDeal) {
            this.endpointService.cor_coy_54(
              newDeal.deal_id,
              this.company.company_id,
              this.imageFile
            ).subscribe({
              next: () => {
                this.saving = false;
                this.toastService.success(this.translateService.instant('companyProfile.addDeal.success'));
                this.modal.close(newDeal);
              },
              error: () => {
                this.saving = false;
                this.toastService.success(this.translateService.instant('companyProfile.addDeal.success'));
                this.modal.close(newDeal);
              }
            });
          } else {
            this.saving = false;
            this.toastService.success(this.translateService.instant('companyProfile.addDeal.success'));
            this.modal.close(newDeal);
          }
        },
        error: (err) => {
          this.saving = false;
          this.toastService.error(err.error?.reason || this.translateService.instant('companyProfile.addDeal.error'));
        }
      });
    }
  }

  close() {
    this.modal.close();
  }
}
